import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, KeyRound, LoaderCircle, LogIn } from "lucide-react";
import { activatePasskeyMfa, beginSignIn, completeSignIn, identityFromTokens, readTokens, refreshTokens, signOut, storeTokens } from "./auth";
import { AdminContext } from "./context";
import { AppShell } from "./components/AppShell";
import { StateCard } from "./components/ui";
import { config, type TokenSet } from "./types";
import { OverviewPage } from "./pages/OverviewPage";
import { PlayersPage, PlayerDetailPage } from "./pages/PlayersPage";
import { CrewsPage, CrewDetailPage } from "./pages/CrewsPage";
import { MarketsPage } from "./pages/MarketsPage";
import { ScannerEconomyPage } from "./pages/ScannerEconomyPage";
import { OperationsPage } from "./pages/OperationsPage";
import { GrantsPage } from "./pages/GrantsPage";
import { MerchantControlsPage } from "./pages/MerchantControlsPage";
import { FuelPromosPage } from "./pages/FuelPromosPage";
import { ScannersPage } from "./pages/ScannersPage";
import { SponsoredSpinsPage } from "./pages/SponsoredSpinsPage";
import { PurseSponsorshipPage } from "./pages/PurseSponsorshipPage";
import { TreasuryOperationsPage } from "./pages/TreasuryOperationsPage";

export function App() {
  const [tokens, setTokens] = useState<TokenSet | null>(() => readTokens());
  const [route, setRoute] = useState(() => location.pathname === "/" ? "/overview" : `${location.pathname}${location.search}`);
  const [authBusy, setAuthBusy] = useState(config.authMode === "cognito" && location.search.includes("code="));
  const [authError, setAuthError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  useEffect(() => { const listener = () => setRoute(`${location.pathname}${location.search}`); addEventListener("popstate", listener); return () => removeEventListener("popstate", listener); }, []);
  useEffect(() => { void completeSignIn().then((next) => { if (next) setTokens(next); }).catch((error: unknown) => setAuthError(error instanceof Error ? error.message : "Sign-in failed.")).finally(() => setAuthBusy(false)); }, []);
  useEffect(() => {
    if (!tokens?.refreshToken) return;
    let timer: number | undefined;
    let refreshInFlight = false;
    let stopped = false;
    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(renew, delay);
    };
    const renew = () => {
      if (refreshInFlight || stopped) return;
      refreshInFlight = true;
      void refreshTokens(tokens)
        .then((next) => { if (!stopped) setTokens(next); })
        .catch(() => {
          if (stopped) return;
          if ((tokens.refreshExpiresAtMs ?? tokens.expiresAtMs) <= Date.now()) {
            storeTokens(null);
            setTokens(null);
            return;
          }
          schedule(30_000);
        })
        .finally(() => { refreshInFlight = false; });
    };
    const renewIfNeeded = () => {
      if (tokens.expiresAtMs - Date.now() <= 5 * 60_000) renew();
    };
    const onVisibilityChange = () => { if (document.visibilityState === "visible") renewIfNeeded(); };
    addEventListener("online", renewIfNeeded);
    document.addEventListener("visibilitychange", onVisibilityChange);
    schedule(Math.max(0, tokens.expiresAtMs - Date.now() - 60_000));
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      removeEventListener("online", renewIfNeeded);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [tokens]);
  const identity = useMemo(() => identityFromTokens(tokens), [tokens]);
  const navigate = (next: string) => { const url = new URL(next, location.origin); history.pushState({}, "", url); setRoute(`${url.pathname}${url.search}`); scrollTo({ top: 0, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" }); };
  if (authBusy || (tokens?.refreshToken && tokens.expiresAtMs <= Date.now())) return <AuthFrame><StateCard icon={<LoaderCircle className="spin" />} title="Completing secure sign-in" detail="Validating the Cognito authorization response." /></AuthFrame>;
  if (authError) return <AuthFrame><StateCard tone="danger" icon={<AlertTriangle />} title="Sign-in could not be completed" detail={authError} action={<button className="button primary" onClick={() => void beginSignIn()}><LogIn />Try again</button>} /></AuthFrame>;
  if (!identity) return <AuthFrame><div className="signin-card"><div className="brand-cube large"><span>M</span></div><p className="eyebrow">MegaCorp Seeker</p><h1>Operations console</h1><p>Authorized operators only. Sign in with your managed account and passkey.</p><button className="button primary full" onClick={() => void beginSignIn()}><KeyRound />Sign in securely</button><small>Self-signup is disabled. Email verification is retained only as a backup factor and for first-time passkey setup.</small></div></AuthFrame>;
  if (!identity.groups.some((group) => group === "megacorp-admin-viewers" || group === "megacorp-admin-operators")) return <AuthFrame><StateCard tone="danger" icon={<AlertTriangle />} title="Admin group required" detail="Your identity authenticated successfully but has no MegaCorp admin role." action={<button className="button secondary" onClick={signOut}>Sign out</button>} /></AuthFrame>;
  const path = route.split("?", 1)[0]!;
  if (path === "/passkeys/complete" && tokens) return <PasskeyActivationFrame tokens={tokens} navigate={navigate} />;
  const context = { identity, tokens, navigate, refreshNonce, refresh: () => setRefreshNonce((value) => value + 1) };
  return <AdminContext.Provider value={context}><a className="skip-link" href="#main-content">Skip to content</a><AppShell path={path}>{renderPage(path)}</AppShell></AdminContext.Provider>;
}

function renderPage(path: string): React.ReactNode {
  const player = path.match(/^\/players\/(.+)$/); if (player) return <PlayerDetailPage wallet={decodeURIComponent(player[1]!)} />;
  const crew = path.match(/^\/crews\/(.+)$/); if (crew) return <CrewDetailPage crewId={decodeURIComponent(crew[1]!)} />;
  if (path.startsWith("/players")) return <PlayersPage />;
  if (path.startsWith("/crews")) return <CrewsPage />;
  if (path.startsWith("/markets")) return <MarketsPage />;
  if (path.startsWith("/scanner-economy")) return <ScannerEconomyPage />;
  if (path.startsWith("/operations")) return <OperationsPage />;
  if (path.startsWith("/treasury-operations")) return <TreasuryOperationsPage />;
  if (path.startsWith("/merchant-controls")) return <MerchantControlsPage />;
  if (path.startsWith("/fuel-promos")) return <FuelPromosPage />;
  if (path.startsWith("/scanners")) return <ScannersPage />;
  if (path.startsWith("/sponsored-spins")) return <SponsoredSpinsPage />;
  if (path.startsWith("/purse-sponsorship")) return <PurseSponsorshipPage />;
  if (path.startsWith("/devnet-grants")) return <GrantsPage />;
  return <OverviewPage />;
}
function AuthFrame({ children }: { children: React.ReactNode }) { return <main className="auth-shell">{children}<p className="auth-build">{config.environment} · {config.buildSha.slice(0, 12)}</p></main>; }

function PasskeyActivationFrame({ tokens, navigate }: { tokens: TokenSet; navigate: (next: string) => void }) {
  const [state, setState] = useState<"working" | "success" | "error">("working");
  const [detail, setDetail] = useState("Finishing passkey registration and retaining email as your recovery factor.");
  const activate = () => {
    setState("working");
    setDetail("Finishing passkey registration and retaining email as your recovery factor.");
    void activatePasskeyMfa(tokens.accessToken)
      .then(() => { setState("success"); setDetail("Your passkey is active. Future sign-ins can use your device biometrics or security key without an authenticator code."); })
      .catch((error: unknown) => { setState("error"); setDetail(error instanceof Error ? error.message : "Passkey sign-in could not be activated."); });
  };
  useEffect(activate, [tokens.accessToken]);
  if (state === "working") return <AuthFrame><StateCard icon={<LoaderCircle className="spin" />} title="Activating passkey" detail={detail} /></AuthFrame>;
  if (state === "error") return <AuthFrame><StateCard tone="danger" icon={<AlertTriangle />} title="Passkey needs one more step" detail={detail} action={<button className="button primary" onClick={activate}><KeyRound />Try again</button>} /></AuthFrame>;
  return <AuthFrame><StateCard icon={<KeyRound />} title="Passkey ready" detail={detail} action={<button className="button primary" onClick={() => navigate("/overview")}>Continue to console</button>} /></AuthFrame>;
}
